// frontend/src/components/MultiAnnotationForm.js
import React, { useState } from 'react';
import api from '../services/api';
import './MultiAnnotationForm.css';

// Define available incident types
const INCIDENT_TYPES = ['Smoking', 'No Helmet', 'Violence', 'Profanity', 'Hate Speech', 'Nudity'];

function MultiAnnotationForm({ videoId, onAnnotationsSubmitted }) {
  // State for the current annotation input
  const [currentAnnotation, setCurrentAnnotation] = useState({
    incidentTypes: [],
    startTime: '',
    endTime: '',
    notes: ''
  });
  // State for the list of annotations added so far
  const [annotations, setAnnotations] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Toggle a checkbox for a given incident type
  const toggleIncidentType = (type) => {
    setCurrentAnnotation((prev) => {
      const current = prev.incidentTypes;
      return {
        ...prev,
        incidentTypes: current.includes(type)
          ? current.filter((t) => t !== type)
          : [...current, type]
      };
    });
  };

  // Update a field in the current annotation
  const updateField = (field, value) => {
    setCurrentAnnotation((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Add the current annotation to the list and reset the form
  const addAnnotation = () => {
    if (
      currentAnnotation.incidentTypes.length === 0 ||
      currentAnnotation.startTime === '' ||
      currentAnnotation.endTime === ''
    ) {
      alert('Please fill in all required fields and select at least one incident type.');
      return;
    }
    
    if (parseFloat(currentAnnotation.startTime) >= parseFloat(currentAnnotation.endTime)) {
      alert('End time must be greater than start time.');
      return;
    }
    
    setAnnotations((prev) => [...prev, currentAnnotation]);
    // Reset current annotation fields
    setCurrentAnnotation({
      incidentTypes: [],
      startTime: '',
      endTime: '',
      notes: ''
    });
  };

  // Remove an annotation from the list
  const removeAnnotation = (index) => {
    setAnnotations((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit all added annotations to the server
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (annotations.length === 0) {
      alert('Please add at least one annotation.');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await api.post(`/videos/${videoId}/annotateMany`, {
        annotations
      });
      if (onAnnotationsSubmitted) onAnnotationsSubmitted(response.data);
      setAnnotations([]);
      setSubmitting(false);
    } catch (error) {
      console.error('Error submitting annotations:', error);
      setSubmitting(false);
    }
  };

  return (
    <div className="multi-annotation-container">
      <div className="annotation-form">
        <div className="form-group">
          <label className="form-label">Content Warnings</label>
          <div className="checkbox-group">
            {INCIDENT_TYPES.map((type) => (
              <label key={type} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={currentAnnotation.incidentTypes.includes(type)}
                  onChange={() => toggleIncidentType(type)}
                />
                {type}
              </label>
            ))}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Time (seconds)</label>
            <input
              type="number"
              className="form-control"
              value={currentAnnotation.startTime}
              onChange={(e) => updateField('startTime', e.target.value)}
              min="0"
              step="0.1"
              placeholder="0.0"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">End Time (seconds)</label>
            <input
              type="number"
              className="form-control"
              value={currentAnnotation.endTime}
              onChange={(e) => updateField('endTime', e.target.value)}
              min="0"
              step="0.1"
              placeholder="0.0"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Notes (optional)</label>
          <textarea
            className="form-control"
            value={currentAnnotation.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            placeholder="Add any additional details about this content warning"
            rows="3"
          />
        </div>
        
        <button 
          type="button" 
          className="btn btn-secondary"
          onClick={addAnnotation}
        >
          <i className="fas fa-plus-circle"></i> Add Warning
        </button>
      </div>

      {annotations.length > 0 && (
        <div className="annotations-list">
          <h4>Added Content Warnings:</h4>
          {annotations.map((ann, index) => (
            <div key={index} className="annotation-summary">
              <div className="annotation-header">
                <div className="annotation-types">
                  {ann.incidentTypes.map(type => (
                    <span key={type} className="annotation-type-badge">{type}</span>
                  ))}
                </div>
                <button 
                  className="btn-icon" 
                  onClick={() => removeAnnotation(index)}
                  title="Remove this warning"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="annotation-details">
                <p className="annotation-time">
                  <i className="fas fa-clock"></i> {ann.startTime}s - {ann.endTime}s
                </p>
                {ann.notes && <p className="annotation-notes">{ann.notes}</p>}
              </div>
            </div>
          ))}
          
          <button 
            type="button" 
            className="btn btn-primary submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Submitting...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Submit All Warnings
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default MultiAnnotationForm;
